import React, { useState, PropsWithChildren, useMemo } from 'react';
import classNames from 'classnames';
import { Study } from '../StudyList';
import { DataStatus } from '../StudyList';
import { ExtractionStatusAction } from '../ExtractionStatusAction';
import { ManageReviewersModal } from './components/ManageReviewersModal/ManageReviewersModal';
import styles from './ExtractorActions.module.scss';
import { groupStatusByReviewer } from './groupStatusByReviewer';
import { Inline, Stack } from 'components/layout';
import {
  dismissToast,
  createToast,
  Link,
  Text,
  TextProps,
  Toast,
  Badge,
  IconList,
  Icon,
} from 'components/core';
import { manageReviewers } from 'query/study/DataExtraction';

export type Reviewer = {
  id: number;
  name: string;
};

export type ExtractorActionsProps = {
  studyId: Study['id'];
  studyTitle: Study['title'];
  permittedPrimaryReviewers: Reviewer[];
  permittedSecondaryReviewers: Reviewer[];
  reviewId: string;
  studyUniqueIndex: number;
  primaryReviewer?: Reviewer;
  secondaryReviewer?: Reviewer;
  dataStatuses: DataStatus[];
  isSingleExtractorMode: boolean;
  hasDataExtractionTemplate: boolean;
  hasQualityAssessmentTemplate: boolean;
  hasPermissionToEditTemplates: boolean;
  hasSuggestions: boolean;
  onRefresh?: () => Promise<void>;
  useLegacyTemplates: boolean;
  isDataExtractionTemplate: boolean;
  isQualityAssessmentTemplate: boolean;
};

export type ManageReviewersParams = {
  keepPrimaryReviewerData: boolean | null;
  keepSecondaryReviewerData: boolean | null;
  primaryReviewerId: number | null;
  secondaryReviewerId: number | null;
};

const TableText = (props: PropsWithChildren<TextProps>) => (
  <Text {...props} size="xs" />
);

const ReviewerName = ({ name }: { name?: string }) => (
  <TableText variant={name ? undefined : 'weak'}>
    {name || 'Unassigned'}
  </TableText>
);

const TemplateActionSection = ({
  hasTemplate,
  link,
  createText,
  actionComponent,
  useLegacyTemplates,
  isActionTemplate,
}: {
  hasTemplate: boolean;
  link: string | null;
  createText: string;
  actionComponent: JSX.Element;
  useLegacyTemplates: boolean;
  isActionTemplate: boolean;
}) => {
  const lowerCaseCreateText = createText.toLowerCase();

  const showReviewTemplateBanner = useLegacyTemplates && isActionTemplate;

  return (
    <>
      <tr>
        <td className={styles.tableHeading} colSpan={3}>
          <div className={styles.tableHeadingContent}>
            <TableText>{createText} form</TableText>
            {showReviewTemplateBanner && (
              <Badge type="neutral" variant="solid">
                Review Template
              </Badge>
            )}
          </div>
        </td>
      </tr>
      {!hasTemplate ? (
        <tr>
          <td colSpan={3}>
            <TableText>
              {link ? (
                <>
                  <Link
                    to={link}
                    variant="primary"
                    aria-label={`Create a ${lowerCaseCreateText} template`}
                  >
                    Create a template
                  </Link>{' '}
                  to begin {lowerCaseCreateText}
                </>
              ) : (
                'Ask your team to create a template'
              )}
            </TableText>
          </td>
        </tr>
      ) : (
        actionComponent
      )}
    </>
  );
};

export const ExtractorActions = ({
  studyId,
  studyTitle,
  permittedPrimaryReviewers,
  permittedSecondaryReviewers,
  reviewId,
  studyUniqueIndex,
  primaryReviewer,
  secondaryReviewer,
  dataStatuses,
  isSingleExtractorMode,
  hasDataExtractionTemplate,
  hasQualityAssessmentTemplate,
  hasPermissionToEditTemplates,
  hasSuggestions,
  onRefresh,
  useLegacyTemplates,
  isDataExtractionTemplate,
  isQualityAssessmentTemplate,
}: ExtractorActionsProps) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const dataExtractionTemplateLink = hasPermissionToEditTemplates
    ? `/reviews/${reviewId}/data_extraction_template`
    : null;
  const qualityAssessmentTemplateLink = hasPermissionToEditTemplates
    ? `/reviews/${reviewId}/qa_template`
    : null;

  const { reviewer1, reviewer2, consensus } = useMemo(
    () => groupStatusByReviewer(dataStatuses),
    [dataStatuses]
  );

  const handleManageReviewers = async ({
    keepPrimaryReviewerData,
    keepSecondaryReviewerData,
    primaryReviewerId,
    secondaryReviewerId,
  }: ManageReviewersParams): Promise<void> => {
    dismissToast();
    try {
      await manageReviewers({
        studyId,
        reviewId,
        keepPrimaryReviewerData,
        keepSecondaryReviewerData,
        primaryReviewerId,
        secondaryReviewerId,
      });

      await onRefresh?.();

      setModalOpen(false);

      createToast(
        <Toast type="success">Reviewers have been updated for study</Toast>
      );
    } catch {
      createToast(
        <Toast type="danger">
          An error occurred. Please try again or refresh the page.
        </Toast>
      );
    }
  };

  const showWarningDataForReviewer = (
    reviewer: Partial<Record<'qa' | 'de', DataStatus>>
  ) => {
    const shouldQAShowWarning = reviewer.qa?.status !== 'not-started';
    const shouldDEShowWarning = reviewer.de?.status !== 'not-started';

    return shouldQAShowWarning || shouldDEShowWarning;
  };

  const areSuggestionsRelevant = (
    reviewer: Partial<Record<'qa' | 'de', DataStatus>>
  ) => {
    if (reviewer?.de === undefined) return false;

    return reviewer.de.permitted && reviewer.de.status !== 'complete';
  };

  const showSuggestions =
    hasSuggestions &&
    hasDataExtractionTemplate &&
    (areSuggestionsRelevant(reviewer1) || areSuggestionsRelevant(reviewer2));

  return (
    <Stack className={styles.wrapper} alignX="center" spacing={2}>
      <table
        className={classNames(styles.table, {
          [styles.singleExtractorTable]: isSingleExtractorMode,
        })}
      >
        <thead>
          <tr>
            <th>
              <TableText>
                {isSingleExtractorMode ? 'Reviewer' : '1st Reviewer'}
              </TableText>
            </th>
            {!isSingleExtractorMode && (
              <>
                <th>
                  <TableText>2nd Reviewer</TableText>
                </th>
                <th>
                  <TableText>Consensus</TableText>
                </th>
              </>
            )}
          </tr>
        </thead>

        <tbody>
          <tr className={styles.reviewers}>
            <td data-testid="reviewer1-name">
              <ReviewerName name={primaryReviewer?.name} />
            </td>
            {!isSingleExtractorMode && (
              <>
                <td data-testid="reviewer2-name">
                  <ReviewerName name={secondaryReviewer?.name} />
                </td>
                <td data-testid="consensus-name">
                  <TableText>Consensus</TableText>
                </td>
              </>
            )}
          </tr>

          <TemplateActionSection
            hasTemplate={hasDataExtractionTemplate}
            link={dataExtractionTemplateLink}
            createText="Data extraction"
            useLegacyTemplates={useLegacyTemplates}
            isActionTemplate={isDataExtractionTemplate}
            actionComponent={
              <>
                <tr>
                  <td className={styles.statusCell} data-testid="de-reviewer1">
                    <ExtractionStatusAction
                      label={reviewer1.de?.label}
                      permitted={reviewer1.de?.permitted}
                      status={reviewer1.de?.status}
                      url={reviewer1.de?.url}
                      ariaLabel={`${reviewer1.de?.label} data extraction for #${studyUniqueIndex}`}
                    />
                  </td>
                  {!isSingleExtractorMode && (
                    <>
                      <td
                        className={styles.statusCell}
                        data-testid="de-reviewer2"
                      >
                        <ExtractionStatusAction
                          label={reviewer2.de?.label}
                          permitted={reviewer2.de?.permitted}
                          status={reviewer2.de?.status}
                          url={reviewer2.de?.url}
                          ariaLabel={`${reviewer2.de?.label} data extraction for #${studyUniqueIndex}`}
                        />
                      </td>
                      <td
                        className={styles.statusCell}
                        data-testid="de-consensus"
                      >
                        <ExtractionStatusAction
                          label={consensus.de?.label}
                          permitted={consensus.de?.permitted}
                          status={consensus.de?.status}
                          url={consensus.de?.url}
                          ariaLabel={`${consensus.de?.label} data extraction for #${studyUniqueIndex}`}
                        />
                      </td>
                    </>
                  )}
                </tr>
                {showSuggestions && (
                  <tr>
                    <td colSpan={isSingleExtractorMode ? 1 : 3}>
                      <Inline
                        className={styles.suggestionBanner}
                        alignX="center"
                        alignY="center"
                        spacing={1}
                        shouldWrap
                      >
                        <Icon
                          icon={IconList.solid.faSparkle}
                          variant="primary"
                        />
                        <Text size="xs" variant="brand-strong">
                          Automated suggestions are ready
                        </Text>
                      </Inline>
                    </td>
                  </tr>
                )}
              </>
            }
          />

          <TemplateActionSection
            hasTemplate={hasQualityAssessmentTemplate}
            link={qualityAssessmentTemplateLink}
            createText="Quality assessment"
            useLegacyTemplates={useLegacyTemplates}
            isActionTemplate={isQualityAssessmentTemplate}
            actionComponent={
              <tr>
                <td className={styles.statusCell} data-testid="qa-reviewer1">
                  <ExtractionStatusAction
                    label={reviewer1.qa?.label}
                    permitted={reviewer1.qa?.permitted}
                    status={reviewer1.qa?.status}
                    url={reviewer1.qa?.url}
                    ariaLabel={`${reviewer1.qa?.label} quality assessment for #${studyUniqueIndex}`}
                  />
                </td>
                {!isSingleExtractorMode && (
                  <>
                    <td
                      className={styles.statusCell}
                      data-testid="qa-reviewer2"
                    >
                      <ExtractionStatusAction
                        label={reviewer2.qa?.label}
                        permitted={reviewer2.qa?.permitted}
                        status={reviewer2.qa?.status}
                        url={reviewer2.qa?.url}
                        ariaLabel={`${reviewer2.qa?.label} quality assessment for #${studyUniqueIndex}`}
                      />
                    </td>

                    <td
                      className={styles.statusCell}
                      data-testid="qa-consensus"
                    >
                      <ExtractionStatusAction
                        label={consensus.qa?.label}
                        permitted={consensus.qa?.permitted}
                        status={consensus.qa?.status}
                        url={consensus.qa?.url}
                        ariaLabel={`${consensus.qa?.label} quality assessment for #${studyUniqueIndex}`}
                      />
                    </td>
                  </>
                )}
              </tr>
            }
          />
        </tbody>
      </table>

      <TableText>
        <Link variant="primary" onClick={() => setModalOpen(true)}>
          Manage Reviewers
        </Link>
      </TableText>
      {isModalOpen && (
        <ManageReviewersModal
          studyId={studyId}
          studyTitle={studyTitle}
          primaryReviewer={primaryReviewer}
          secondaryReviewer={secondaryReviewer}
          permittedPrimaryReviewers={permittedPrimaryReviewers}
          permittedSecondaryReviewers={permittedSecondaryReviewers}
          isModalOpen={isModalOpen}
          setModalOpen={setModalOpen}
          manageReviewers={handleManageReviewers}
          isSingleExtractorMode={isSingleExtractorMode}
          showWarningDataForPrimaryReviewer={showWarningDataForReviewer(
            reviewer1
          )}
          showWarningDataForSecondaryReviewer={showWarningDataForReviewer(
            reviewer2
          )}
        />
      )}
    </Stack>
  );
};
